import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useWindowWidth} from "../../../hooks/useWindowWidth";
import {applicationSelector, fetchAndroidAppList, fetchIOSAppList} from "../../../redux/slices/applicationSlice";
import {is_tech_user} from "../../../storage/user";
import CustomSkeleton from "../../../components/UiKit/CustomSkeleton/CustomSkeleton";
import EmptyList from "../../../components/EmptyList/EmptyList";
import s from "./appCatalog.module.scss";
import IosAppCard from "./IosAppCard/IosAppCard";
import {
    fetchGeosList,
    globalDataSelector,
} from "../../../redux/slices/globalDataSlice";
import useGetSearchParams from "../../../hooks/useGetSearchParams";
import useSetSearchParams from "../../../hooks/useSetSearchParams";
import {useDebounce} from "../../../hooks/useDebounce";
import {search} from "../Statistics/IOS/IOSClicksStats/IOSClicksStats";
import Header from "../../../components/Partials/Header/Header";
import {ACTIVE_FLOWS, ANDROID, EXPIRED_FLOWS, IOS} from "../../../utils/vars/staticVariables";
import SvgSelector from "../../../components/SvgSelector/SvgSelector";
import {flowsTabs} from "../Flows/Flows";
import i18n from "i18next";
import {Trans, useTranslation} from "react-i18next";
import AndroidAppCard from "./AndroidAppCard/AndroidAppCard";
import PleaseNotePopup from "../../../components/UiKit/Popups/PleaseNotePopup/PleaseNotePopup";

const appCatalogTabs = [IOS, ANDROID]

const AppCatalog = () => {
    const [isPleaseNotePopupActive, setIsPleaseNotePopupActive] = useState(false);
    const [pleaseNoteOnContinue, setPleaseNoteOnContinue] = useState(null);

    const underlineRef = useRef(null);

    const dispatch = useDispatch();
    const {appList, appListLoading} = useSelector(applicationSelector);
    const {isCollapsedSidebar, geoListPending} =
        useSelector(globalDataSelector);

    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const {search: urlSearchValue} = useGetSearchParams();

    const updateSearchParams = useSetSearchParams(setSearchParams);

    const [inputSearchValue, setInputSearchValue] = useDebounce((value) => {
        if (value) {
            updateSearchParams(search, value);
        } else {
            updateSearchParams(search, value, true);
        }
    }, 600);

    const currentTab = (() => {
        const contentParam = searchParams.get("content");

        if (appCatalogTabs.find(item => item === contentParam)) {
            return contentParam;
        } else {
            return appCatalogTabs[0];
        }
    })();

    const filteredAppList = useMemo(() => {
        if (currentTab === ANDROID) {
            return appList;
        } else {
            if (is_tech_user) {
                return appList.filter((item) => item?.is_alive === true);
            }
            return appList;
        }
    }, [appList, currentTab]);

    const width = useWindowWidth();

    useEffect(() => {

        if (currentTab === IOS) {
            dispatch(fetchIOSAppList(searchParams));
        }

        if (currentTab === ANDROID) {
            dispatch(fetchAndroidAppList(searchParams));
        }

    }, [dispatch, searchParams]);

    useEffect(() => {
        dispatch(fetchGeosList());
    }, [dispatch]);

    useEffect(() => {
        if (!urlSearchValue) {
            setInputSearchValue("");
        } else {
            setInputSearchValue(urlSearchValue);
        }
    }, [searchParams]);

    const updateUnderline = () => {
        const activeBtn = document.querySelector(`.${CSS.escape(s.active)}`);
        if (activeBtn && underlineRef.current) {
            const {left, width} = activeBtn.getBoundingClientRect();
            const parentLeft = activeBtn.parentElement.getBoundingClientRect().left;
            underlineRef.current.style.width = `${width}px`;
            underlineRef.current.style.transform = `translateX(${left - parentLeft}px)`;
        }
    };

    useEffect(() => {
        updateUnderline();
        window.addEventListener("resize", updateUnderline);

        return () => {
            window.removeEventListener("resize", updateUnderline);
        };
    }, [currentTab, i18n.language]);

    let skeletonsCount;

    if (isCollapsedSidebar === "true") {
        if (width < 2288) {
            skeletonsCount = 9;
        }
        if (width >= 2288 && width < 2815) {
            skeletonsCount = 12;
        }

        if (width >= 2815 && width < 3492) {
            skeletonsCount = 15;
        }

        if (width > 3341 && width < 3868) {
            skeletonsCount = 18;
        }

        if (width > 3868) {
            skeletonsCount = 21;
        }
    } else {
        if (width < 2438) {
            skeletonsCount = 9;
        }
        if (width > 2437 && width < 2965) {
            skeletonsCount = 12;
        }

        if (width > 2964 && width < 3492) {
            skeletonsCount = 15;
        }

        if (width > 3491 && width < 4019) {
            skeletonsCount = 18;
        }

        if (width > 4018) {
            skeletonsCount = 21;
        }
    }

    const skeletons = (styles) => {
        return [...new Array(skeletonsCount)].map((_, index) => (
            <CustomSkeleton styles={styles} key={index}/>
        ));
    };

    const pageWidth = useWindowWidth();

    return (
        <div
            className={`${s.appCatalogWrapper} ${isCollapsedSidebar === "true" ? s.collapsedSidebar : ""}`}
        >
            <PleaseNotePopup
                active={isPleaseNotePopupActive}
                setActive={setIsPleaseNotePopupActive}
                description={
                    <Trans
                        i18nKey="redirector_http_warning"
                        components={[<React.Fragment key="0"/>, <strong key="1"/>]}
                    />
                }
                onContinue={pleaseNoteOnContinue}
            />
            <Header
                searchValue={inputSearchValue}
                setSearchValue={setInputSearchValue}
                isSearch
            />
            <div className={s.pageContent}>
                <div className={s.switchPageContentWrapper}>

                    <div className={s.switchPageContentContainer}>
                        <button
                            className={`${s.pageBtn} ${
                                currentTab === IOS ? s.active : ""
                            }`}
                            onClick={() =>
                                updateSearchParams("content", IOS)
                            }
                        >
                            <SvgSelector id="app-store-icon"/>
                            IOS
                        </button>

                        <button
                            className={`${s.pageBtn} ${
                                currentTab === ANDROID ? s.active : ""

                            }`}
                            onClick={() =>
                                updateSearchParams("content", ANDROID)
                            }
                        >
                            <SvgSelector id="google-play-limit-icon"/>
                            Android
                        </button>
                        <div className={s.underline} ref={underlineRef}></div>
                    </div>
                </div>

                <div className={s.appCatalog}>
                    {!appListLoading && !geoListPending && filteredAppList.length < 1 ? (
                        <EmptyList/>
                    ) : (
                        <div
                            className={`${s.content} ${isCollapsedSidebar === "true" ? s.collapsedSidebar : ""}`}
                        >
                            {appListLoading || geoListPending
                                ? skeletons(s.skeletonCard)
                                : filteredAppList.map(item => {
                                    // вычисляем ключ в зависимости от платформы
                                    const key = currentTab === ANDROID ? item.bundle_id : item.id;
                                    // общие пропсы для обеих карточек
                                    const commonProps = {
                                        ...item,
                                        navigate,
                                        pageWidth,
                                        isAndroid: currentTab === ANDROID,
                                    };

                                    return currentTab === ANDROID
                                        ? <AndroidAppCard {...commonProps} key={key} />
                                        : <IosAppCard
                                            {...commonProps}
                                            key={key}
                                            onFlowsClick={(appName) => {
                                                if (appName === 'Float Talk') {
                                                    setPleaseNoteOnContinue(() => () =>
                                                        navigate(`/my-flows?application_name=${appName}`)
                                                    );
                                                    setIsPleaseNotePopupActive(true);
                                                } else {
                                                    navigate(`/my-flows?application_name=${appName}`);
                                                }
                                            }}
                                        />;
                                })
                            }
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppCatalog;
